import {format} from 'date-fns';
import {toast} from 'react-toastify';
import React, {FC} from 'react';
import {IOrganization} from '@verdocs/js-sdk';
import {ArrowsRightLeftIcon, ClipboardDocumentIcon, DocumentDuplicateIcon, EnvelopeIcon, UsersIcon} from '@heroicons/react/24/outline';
import {OrgAvatar} from './OrgAvatar';

export interface IOrgCardStats {
  members: number;
  envelopes: number;
  templates: number;
}

interface OrgCardProps {
  org: IOrganization;
  badge?: string;
  stats?: IOrgCardStats;
  canSwitch: boolean;
  onSwitch: () => void;
}

const StatCell: FC<{icon: FC<{className?: string}>; label: string; value: number; hint?: string}> = ({
  icon: Icon,
  label,
  value,
  hint,
}) => (
  <div className="px-4 py-3" title={hint}>
    <div className="flex items-center gap-1.5 text-gray-400">
      <Icon className="h-3.5 w-3.5" />
      <span className="text-[11px] font-medium uppercase tracking-wide">{label}</span>
    </div>
    <div className={`mt-0.5 text-lg font-semibold ${value > 0 ? 'text-gray-900' : 'text-gray-400'}`}>{value}</div>
  </div>
);

export const OrgCard: FC<OrgCardProps> = ({org, badge, stats, canSwitch, onSwitch}) => {
  const handleCopyId = () => {
    navigator.clipboard
      .writeText(org.id)
      .then(() => toast.success('Organization ID copied'))
      .catch((e) => console.log('Error copying to clipboard', e));
  };

  return (
    <div className="flex flex-col rounded-xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-center gap-3 p-4">
        <OrgAvatar org={org} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="truncate text-[15px] font-semibold text-gray-900">{org.name}</h3>
            {badge && (
              <span className="whitespace-nowrap rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] font-medium text-gray-500">{badge}</span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-gray-500">Created {format(new Date(org.created_at), 'MMM d, yyyy')}</p>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-3 divide-x divide-gray-100 border-t border-gray-100">
          <StatCell icon={UsersIcon} label="Members" value={stats.members} />
          <StatCell icon={EnvelopeIcon} label="Envelopes" value={stats.envelopes} hint="Past 90 days" />
          <StatCell icon={DocumentDuplicateIcon} label="Templates" value={stats.templates} hint="Past 90 days" />
        </div>
      )}

      <div className="flex items-center justify-between rounded-b-xl border-t border-gray-100 bg-gray-50 px-4 py-3">
        <button
          onClick={onSwitch}
          disabled={!canSwitch}
          title={canSwitch ? undefined : 'You do not have a member profile in this organization'}
          className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40">
          <ArrowsRightLeftIcon className="h-3.5 w-3.5" />
          Switch to this org
        </button>

        <button
          onClick={handleCopyId}
          title={org.id}
          className="inline-flex items-center gap-1 text-xs font-medium text-gray-400 transition-colors hover:text-gray-600">
          <ClipboardDocumentIcon className="h-3.5 w-3.5" />
          Copy ID
        </button>
      </div>
    </div>
  );
};
